import { useEffect, useState } from 'react';
import { getUserData } from 'services/UserData';
import { PriceDisplayRule } from '../../../../../../Roc.SharedClientApp/types/commerce/display-price';
import { GetPriceForMinimumAvailableQuantity, GetPricingLine } from '../../pricing/pricing-result';
import { useProductDetailsContext } from '../ProductDetailsContext';
import { ProductTableProductDetails, ProductType } from '../types';

export interface UseTapeCaseProductDetails {
	state: UseTapeCaseProductDetailsState;
	actor: UseTapeCaseProductDetailsActor;
}

/**
 * Use TapeCase Product Details Context State
 */
export interface UseTapeCaseProductDetailsState {
	/**
	 * The data for product table (on product series page) that constists of simple products.
	 */
	productSeriesTableData: ProductTableProductDetailsRow[] | null;
	productUrl: string;
}

export interface ProductTableProductDetailsRow {
	/**
	 * The original product details dto
	 */
	details: ProductTableProductDetails;
	/**
	 * The value for Dimensions 1 filter applicable for this product
	 */
	dimension1FilterValue: string;
	/**
	 * The value for Dimensions 2 filter applicable for this product
	 */
	dimension2FilterValue: string;
	/**
	 * The value for Color filter applicable for this product
	 */
	colorFilterValue: string;
}

/**
 * Use TapeCase Product Details Context Actor
 */
export interface UseTapeCaseProductDetailsActor {
	/**
	 * Updates data for product table with the provided value.
	 */
	setProductSeriesTableData: (data: ProductTableProductDetailsRow[] | null) => void;
	/**
	 * Evaluates if contract pricing is available -> this means there is a chance that
	 * the customer will see a different pricing after successfully logging in.
	 */
	isContractPricingAvailable: () => boolean | undefined;
}

/**
 * Reusable hook to access TapeCase product detail related endpoints on the server, as well as helping manage
 * product detail information.
 */
export default function useTapeCaseProductDetails(): UseTapeCaseProductDetails {
	// can't really use isUserSignedIn flag from ROC Product Details context
	// because it is either true/false and there is no way to determine
	// if loading user data is in progress (like having undefined alternative which is set as long as the data is loading),
	// this let's us hide Login link until we are certainly sure user is not logged in,
	// otherwise it would be displayed and once data is loaded then it would get hidden
	// which provides poor UX
	const {
		state: { productDetails },
		actor: { isLoadingAny },
	} = useProductDetailsContext();

	const [isLoggedIn, setIsLoggedIn] = useState<boolean>();
	const [productSeriesTableData, setProductSeriesTableData] = useState<ProductTableProductDetailsRow[] | null>(null);

	const checkIfIsLoggedIn = async () => {
		try {
			const data = (await getUserData()).data;
			setIsLoggedIn(data && data.summary && data.summary.isSignedIn);
		} catch (error) {}
	};

	const getPricingDetail = () => {
		if (productDetails.displayPricing == null) {
			return undefined;
		}
		const selectedPricingLineId =
			productDetails.pricingLines &&
			productDetails.pricingLines.selected &&
			productDetails.pricingLines.selected.id;

		const displayPriceLine = GetPricingLine(
			productDetails.displayPricing,
			productDetails.id,
			selectedPricingLineId,
		);

		return displayPriceLine && GetPriceForMinimumAvailableQuantity(displayPriceLine.prices);
	};

	const isAnyProductPurchasable = (): boolean => {
		if (productSeriesTableData == null || Object.keys(productSeriesTableData).length == 0) {
			return false;
		}

		let hasValidItem = false;
		for (const key in productSeriesTableData) {
			const simpleProductDetails = productSeriesTableData[key];
			if (
				simpleProductDetails.details.summary.type != ProductType.Group &&
				simpleProductDetails.details.pricingLine.length > 0
			) {
				for (const pricingLineId in simpleProductDetails.details.pricingLine) {
					const pricingLine = simpleProductDetails.details.pricingLine[pricingLineId];
					for (const qtyBreakIndex in pricingLine.prices) {
						if (pricingLine.prices[qtyBreakIndex].displayRule != PriceDisplayRule.Missing) {
							hasValidItem = true;
							break;
						}
					}
				}
			}
		}

		return hasValidItem;
	};

	const isContractPricingAvailable = () => {
		if (isLoggedIn == undefined) {
			return;
		}

		switch (productDetails.summary.type) {
			case ProductType.Group:
				// for groups, pricing can be different (contract pricing) after logging in
				// only when there is data for product table (this means there are simple products associated to this group)
				// and product series is NOT discontinued
				// and user is not logged in
				return isAnyProductPurchasable() && !isLoggedIn;
			default:
				const pricing = getPricingDetail();
				// for simples, pricing can be different when dispaly price is no longer loading
				// there is a pricing line (not missing one), product is NOT discontinued
				// and user is not logged in
				return (
					!isLoadingAny['displayPrice'] &&
					pricing != undefined &&
					pricing != null &&
					pricing.displayRule != PriceDisplayRule.Missing &&
					!isLoggedIn
				);
		}
	};

	useEffect(() => {
		checkIfIsLoggedIn();
	}, []);

	return {
		state: {
			productSeriesTableData,
			productUrl: productDetails.summary.productUrl,
		},
		actor: {
			setProductSeriesTableData,
			isContractPricingAvailable,
		},
	};
}
